import { useEffect, useRef, useState } from 'react'

import {
  ESSENTIALS_COURSE,
  ESSENTIALS_COURSE_SITE_CATEGORY_MAP,
  ESSENTIALS_GET_COURSES_QUERY,
  EssentialsCourseRaw,
  isLanguageEnglish,
  runEssentialsQuery,
} from '@lyrahealth-inc/shared-app-logic'

export const useGetEssentialsCourses = ({
  essentialsCmsUrl,
  userDisplayLanguage,
  essentialsCourses,
  logError,
  skipFetch,
}: {
  essentialsCmsUrl: string
  userDisplayLanguage: string
  essentialsCourses?: ESSENTIALS_COURSE[]
  logError?: (error: Error) => void
  skipFetch?: boolean
}) => {
  const [courses, setCourses] = useState<EssentialsCourseRaw[]>([])
  const hasFetchedCourses = useRef(false)
  const isFetchingCourses = useRef(false)

  // Use english language only and let essentials site handle user language from header
  const locale = userDisplayLanguage ?? 'en-US'
  const isUserActiveEnglish = isLanguageEnglish(locale)

  const getEssentialCourseCallback = (course: string, userLocale: string) => {
    return runEssentialsQuery({
      locale: userLocale,
      slug: course,
      query: ESSENTIALS_GET_COURSES_QUERY,
      essentialsCmsUrl,
      logError: logError,
      url: '?getCourses=true',
      siteCategory: ESSENTIALS_COURSE_SITE_CATEGORY_MAP[course],
    })
  }

  useEffect(() => {
    if (
      skipFetch ||
      !(essentialsCourses && essentialsCmsUrl && locale) ||
      (essentialsCourses && essentialsCourses.length === 0) ||
      hasFetchedCourses.current ||
      isFetchingCourses.current
    ) {
      return
    }

    const getCourses = async () => {
      isFetchingCourses.current = true
      const result = await Promise.all(essentialsCourses.map((course) => getEssentialCourseCallback(course, locale)))
      const englishResults = await Promise.all(
        isUserActiveEnglish ? [] : essentialsCourses.map((course) => getEssentialCourseCallback(course, 'en-US')),
      )
      const mixedResults: EssentialsCourseRaw[] = []
      if (!isUserActiveEnglish) {
        // Combine english with user local language
        englishResults.forEach((englishCourseRawArr) => {
          const englishCourseRaw = englishCourseRawArr[0]
          if (englishCourseRaw) {
            let mixedResultsCourseRaw: EssentialsCourseRaw = {
              ...englishCourseRaw,
              isEnglish: true,
            }

            const localCourseRawArr = result.find((localeCourses) => {
              const localCourseObj = localeCourses[0]
              return localCourseObj && localCourseObj.id === englishCourseRaw.id
            })

            if (localCourseRawArr && localCourseRawArr.length > 0) {
              // Found user local language course
              const localCourseRaw = localCourseRawArr[0]
              mixedResultsCourseRaw = { ...localCourseRaw, uri: englishCourseRaw.uri, isEnglish: false }
            }
            mixedResults.push(mixedResultsCourseRaw)
          }
        })
      } else {
        result.forEach((courseResult) => {
          if (courseResult && courseResult.length) {
            const englishCourseResult = courseResult[0]
            mixedResults.push({
              ...englishCourseResult,
              isEnglish: true,
            })
          }
        })
      }

      setCourses(mixedResults)
      isFetchingCourses.current = false
      hasFetchedCourses.current = true
    }
    getCourses()
  }, [essentialsCmsUrl, essentialsCourses, locale, logError, skipFetch])

  return courses
}
